/**
 * PlatformSettingsRepo — typed repository over {@link DocStorePort} for the
 * single `platformSettings/global` doc. Superadmin-editable; every reader gets
 * a fully-populated {@link PlatformSettings} even before the doc exists, via the
 * typed {@link DEFAULT_PLATFORM_SETTINGS} (merged over any stored partial so a
 * newly-added field always has a value).
 */
import { type DocStorePort } from '@djgg/core';
import { AUDIO_GENERATION_DISABLED } from '../audioGeneration.js';
import { platformAdminSettingsDoc, platformSettingsDoc } from '../paths.js';
import type { EngineRef, PlatformAdminSettings, PlatformSettings } from '../types.js';

/** The default engine used until a superadmin overrides it. */
export const DEFAULT_ENGINE: EngineRef = { provider: 'anthropic', model: 'claude-opus-4-8' };
/** An absolute worker-start deadline, not a per-branch timeout. This stays
 * comfortably inside the 60-minute request wall and leaves code/audit/publish
 * headroom even when Admin selects the maximum. */
export const MAX_FIRST_RELEASE_MEDIA_WAIT_MS = 1_200_000;
export const MAX_ASSET_GO_LIVE_BUDGET_MS = 1_200_000;
export const DEFAULT_PLATFORM_ADMIN_SETTINGS: PlatformAdminSettings = {
  revision: 0,
  mediaReviewPilotCreationIds: [],
  measuredFirstPlayablePilotUids: [],
};

/** Typed defaults — the fallback for every settings field. */
export const DEFAULT_PLATFORM_SETTINGS: PlatformSettings = {
  revision: 0,
  // The public creation journey is universal; historical values remain readable.
  creationExperience: 'deej-led',
  creationFeatures: {
    creationFilms: true,
    workshopSelections: true,
    mediaReviewPilot: false,
    videoReferences: true,
    suggestionPass: true,
    diagnosisLoop: true,
    qualityLoop: false,
    assetGoLiveBudget: true,
  },
  defaultEngine: DEFAULT_ENGINE,
  buildConcurrency: 2,
  maxVersionsPerDay: 20,
  maxAssetGensPerDay: 30,
  maxConceptAnalysesPerDay: 40,
  // On-demand playtest audits (CPU playtest + demo mode): modest — a member
  // re-auditing the same live version dedupes onto the outstanding job anyway.
  maxAuditsPerDay: 10,
  galleryFeaturedSlugs: [],
  // Editors' Pick starts with no admin overrides; the weekly reviewer fills
  // the active list and stamps lastRunAt/edition on its first run.
  editorsPick: { pinned: [], excluded: [] },
  motd: '',
  // The autonomous critic is dormant by default: one normal build pass.
  maxQualityPasses: 1,
  qualityBar: 0.7,
  // Asset go-live budget (pipeline-improvement initiative): after the code
  // gates pass, a build waits at most this long for the tailored asset library
  // before banking the stragglers and publishing (async completion re-binds
  // them). The bounded wait can be changed above without a redeploy.
  assetGoLiveBudgetMs: 120_000,
  // A first release should audit the approved media, not race a short iterate
  // budget and then cancel healthy work. Admin can tune this without deploy.
  firstReleaseMediaWaitMs: 900_000,
  // Provider-generated audio is OFF until the owner enables it in Admin.
  audioGeneration: AUDIO_GENERATION_DISABLED,
};

export class PlatformSettingsRepo {
  constructor(private readonly store: DocStorePort) {}

  /**
   * Read the global settings, merged over {@link DEFAULT_PLATFORM_SETTINGS} so
   * missing/absent fields resolve to their defaults (never undefined).
   */
  async get(): Promise<PlatformSettings> {
    const stored = await this.store.getDoc<Partial<PlatformSettings>>(platformSettingsDoc());
    return normalizePlatformSettings(stored);
  }

  /**
   * Merge a partial settings patch onto the global doc (create-or-merge). The
   * caller is expected to have passed `requireRole(claims, 'superadmin')`; the
   * rules are the enforcing line.
   */
  async update(patch: Partial<PlatformSettings>): Promise<void> {
    await this.store.mergeDoc<PlatformSettings & Record<string, unknown>>(
      platformSettingsDoc(),
      patch as Partial<PlatformSettings & Record<string, unknown>>,
    );
  }

  /** Protected rollout details. The database permits this document only to
   * superadmins and trusted server connections. */
  async getAdminSettings(): Promise<PlatformAdminSettings> {
    const stored = await this.store.getDoc<Partial<PlatformAdminSettings>>(
      platformAdminSettingsDoc(),
    );
    return normalizePlatformAdminSettings(stored);
  }

  async updateAdminSettings(patch: Partial<PlatformAdminSettings>): Promise<void> {
    await this.store.mergeDoc<PlatformAdminSettings & Record<string, unknown>>(
      platformAdminSettingsDoc(),
      patch as Partial<PlatformAdminSettings & Record<string, unknown>>,
    );
  }
}

/** Normalize one raw/legacy public singleton without performing another read. */
export function normalizePlatformSettings(
  stored: Partial<PlatformSettings> | null | undefined,
): PlatformSettings {
  if (!stored) return structuredClone(DEFAULT_PLATFORM_SETTINGS);
  return {
      revision:
        typeof stored.revision === 'number' && Number.isInteger(stored.revision) && stored.revision >= 0
          ? stored.revision
          : DEFAULT_PLATFORM_SETTINGS.revision,
      // Retired rollout switches cannot send fresh creators through a different flow.
      creationExperience: 'deej-led',
      creationFeatures: {
        creationFilms: booleanOrDefault(
          stored.creationFeatures?.creationFilms,
          DEFAULT_PLATFORM_SETTINGS.creationFeatures.creationFilms,
        ),
        workshopSelections: booleanOrDefault(
          stored.creationFeatures?.workshopSelections,
          DEFAULT_PLATFORM_SETTINGS.creationFeatures.workshopSelections,
        ),
        mediaReviewPilot: booleanOrDefault(
          stored.creationFeatures?.mediaReviewPilot,
          DEFAULT_PLATFORM_SETTINGS.creationFeatures.mediaReviewPilot,
        ),
        videoReferences: booleanOrDefault(
          stored.creationFeatures?.videoReferences,
          DEFAULT_PLATFORM_SETTINGS.creationFeatures.videoReferences,
        ),
        suggestionPass: booleanOrDefault(
          stored.creationFeatures?.suggestionPass,
          DEFAULT_PLATFORM_SETTINGS.creationFeatures.suggestionPass,
        ),
        diagnosisLoop: booleanOrDefault(
          stored.creationFeatures?.diagnosisLoop,
          DEFAULT_PLATFORM_SETTINGS.creationFeatures.diagnosisLoop,
        ),
        qualityLoop: booleanOrDefault(
          stored.creationFeatures?.qualityLoop,
          DEFAULT_PLATFORM_SETTINGS.creationFeatures.qualityLoop,
        ),
        assetGoLiveBudget: booleanOrDefault(
          stored.creationFeatures?.assetGoLiveBudget,
          DEFAULT_PLATFORM_SETTINGS.creationFeatures.assetGoLiveBudget,
        ),
      },
      defaultEngine: stored.defaultEngine ?? { ...DEFAULT_PLATFORM_SETTINGS.defaultEngine },
      buildConcurrency: stored.buildConcurrency ?? DEFAULT_PLATFORM_SETTINGS.buildConcurrency,
      maxVersionsPerDay: stored.maxVersionsPerDay ?? DEFAULT_PLATFORM_SETTINGS.maxVersionsPerDay,
      maxAssetGensPerDay:
        stored.maxAssetGensPerDay ?? DEFAULT_PLATFORM_SETTINGS.maxAssetGensPerDay,
      maxConceptAnalysesPerDay:
        stored.maxConceptAnalysesPerDay ?? DEFAULT_PLATFORM_SETTINGS.maxConceptAnalysesPerDay,
      maxAuditsPerDay: stored.maxAuditsPerDay ?? DEFAULT_PLATFORM_SETTINGS.maxAuditsPerDay,
      galleryFeaturedSlugs: stored.galleryFeaturedSlugs ?? [
        ...DEFAULT_PLATFORM_SETTINGS.galleryFeaturedSlugs,
      ],
      // Merge the config field-by-field so a partial stored doc (only pins, or
      // only lastRunAt) still yields array pins/excludes, never undefined.
      editorsPick: {
        pinned: stored.editorsPick?.pinned ?? [],
        excluded: stored.editorsPick?.excluded ?? [],
        lastRunAt: stored.editorsPick?.lastRunAt,
        edition: stored.editorsPick?.edition,
      },
      motd: stored.motd ?? DEFAULT_PLATFORM_SETTINGS.motd,
      maxQualityPasses: stored.maxQualityPasses ?? DEFAULT_PLATFORM_SETTINGS.maxQualityPasses,
      qualityBar: stored.qualityBar ?? DEFAULT_PLATFORM_SETTINGS.qualityBar,
      assetGoLiveBudgetMs:
        stored.assetGoLiveBudgetMs ?? DEFAULT_PLATFORM_SETTINGS.assetGoLiveBudgetMs,
      firstReleaseMediaWaitMs:
        stored.firstReleaseMediaWaitMs ?? DEFAULT_PLATFORM_SETTINGS.firstReleaseMediaWaitMs,
      // Read per kind, so a partially-stored doc can never resolve a provider
      // flag to anything but an explicit true.
      audioGeneration: {
        music: stored.audioGeneration?.music === true,
        sfx: stored.audioGeneration?.sfx === true,
      },
  };
}

/** Normalize one raw/legacy private singleton without performing another read. */
export function normalizePlatformAdminSettings(
  stored: Partial<PlatformAdminSettings> | null | undefined,
): PlatformAdminSettings {
  return {
    revision:
      typeof stored?.revision === 'number' && Number.isInteger(stored.revision) && stored.revision >= 0
        ? stored.revision
        : DEFAULT_PLATFORM_ADMIN_SETTINGS.revision,
    mediaReviewPilotCreationIds: cleanCreationIds(stored?.mediaReviewPilotCreationIds),
    measuredFirstPlayablePilotUids: cleanCreationIds(stored?.measuredFirstPlayablePilotUids),
  };
}

function booleanOrDefault(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function cleanCreationIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return [...new Set(value
    .filter((id): id is string => typeof id === 'string')
    .map((id) => id.trim())
    .filter(Boolean))];
}
